import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { Grid } from 'src/components/Grid';
import { Stack } from 'src/components/Stack';
import { ToggleButton } from 'src/components/ToggleButton';
import { ToggleButtonGroup } from 'src/components/ToggleButtonGroup';
import { Typography } from 'src/components/Typography';
import { FormDatePicker } from 'src/shared/Forms/Fields/FormDatePicker';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import { DEFAULT_RECURRING_DAYS, EditEventFormValues } from './_types';

interface RecurringSettingsSectionProps {
  isRecurring?: boolean;
}

export const RecurringSettingsSection: FC<RecurringSettingsSectionProps> = ({
  isRecurring = false, // for existing recurring sessions (as opposed to sessions you are just not converting to recurring)
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.editEventDialog.recurringSettings' });
  const { values, setFieldValue } = useFormikContext<EditEventFormValues>();

  const RECURRING_TYPE_OPTIONS = [
    { value: 'week', label: t('weekly') },
    { value: 'month_on_date', label: t('monthlyDate') },
    { value: 'month_on_day', label: t('monthlyDay') },
  ];

  const dayButtons = DEFAULT_RECURRING_DAYS.map((day) => ({
    ...day,
    selected: values.recurringDays.includes(day.id),
  }));

  const handleDayToggle = (dayId: number) => {
    const currentDays = values.recurringDays;
    const newDays = currentDays.includes(dayId)
      ? currentDays.filter((d) => d !== dayId)
      : [...currentDays, dayId].sort();
    setFieldValue('recurringDays', newDays);
  };

  return (
    <Stack spacing={2}>
      {!isRecurring && (
        <Checkbox
          useSwitch
          label={t('makeRecurring')}
          checked={values.isRecurring}
          onChange={(e) => setFieldValue('isRecurring', e.target.checked)}
        />
      )}

      {values.isRecurring && (
        <Box
          component="fieldset"
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            m: 0,
          }}
        >
          <Typography variant="h6" component="legend">
            {t('title')}
          </Typography>

          {isRecurring && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('isRecurringNote')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {t('onlyThisSessionNote')}
              </Typography>
            </>
          )}

          <Stack spacing={3}>
            <FormTextField
              name="recurringType"
              label={t('repeat')}
              options={RECURRING_TYPE_OPTIONS}
            />

            {values.recurringType === 'week' && (
              <>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {t('repeatOn')}
                  </Typography>
                  <ToggleButtonGroup
                    value={values.recurringDays}
                    aria-label="Days of week"
                    size="small"
                  >
                    {dayButtons.map((day) => (
                      <ToggleButton
                        key={day.id}
                        value={day.id}
                        selected={day.selected}
                        onClick={() => handleDayToggle(day.id)}
                        sx={{
                          minWidth: 40,
                          px: 1,
                        }}
                      >
                        {day.shortName}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormTextField
                      name="recurringCount"
                      label={t('repeatEveryWeeks')}
                      type="number"
                      placeholder="1"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormDatePicker name="endAt" label={t('endDate')} />
                  </Grid>
                </Grid>
              </>
            )}

            {(values.recurringType === 'month_on_date' ||
              values.recurringType === 'month_on_day') && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormTextField
                    name="recurringCount"
                    label={t('repeatEveryMonths')}
                    type="number"
                    placeholder="1"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormDatePicker name="endAt" label={t('endDate')} />
                </Grid>
              </Grid>
            )}

            <Box>
              <Checkbox
                label={t('allowBookAllRecurring')}
                checked={values.bookRecurring}
                onChange={(e) =>
                  setFieldValue('bookRecurring', e.target.checked)
                }
              />
            </Box>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
